import { parseGrPackage } from '@growing/domain-rules';
import { type Transport, currentGen, newRequestKey } from './transport.js';

export const reviewFields = [
  ['school', '학교'], ['course', '과정·학년'], ['scope', '시험 범위'],
  ['exclusions', '제외 지문·범위 예외'], ['sourceVersion', '원문·기출·답안 버전'],
  ['prediction', '최초 예측: 선택 문장·예상 변형·근거'],
  ['result', '사후 검수: 실제 변형·최초 예측과 차이'],
  ['acceptance', '정답 인정 조건·복수 정답·서술형 조건'],
  ['distractors', '오답 보기 근거·과장·방향 반전·답 노출 점검'],
] as const;
export type ReviewDraft = Record<typeof reviewFields[number][0], string>;
export function prepareContent(text: string, review: ReviewDraft) {
  let raw: Record<string, unknown>;
  try { raw = JSON.parse(text); } catch { throw new Error('문항 JSON 형식을 확인해 주세요.'); }
  const checked = parseGrPackage(raw);
  if (!checked.ok) throw new Error(checked.errors.join(' / '));
  for (const [key, label] of reviewFields) if (!review[key]?.trim()) throw new Error(`${label}을 입력해 주세요. 해당 없으면 해당 없음으로 기록합니다.`);
  // meta is excluded from content fingerprints. Review records belong in the immutable content, not meta.
  if ('editorial_review' in raw) throw new Error('이미 검수 기록이 포함된 자료입니다. 원본 자료를 사용해 새 버전으로 등록해 주세요.');
  const pkg = { ...raw, editorial_review: { schema: 1, ...review } };
  if (new TextEncoder().encode(JSON.stringify({ package: pkg })).length > 60 * 1024) throw new Error('문항 자료가 너무 큽니다. 문항 단위로 나누어 주세요.');
  return { raw: pkg, parsed: checked.pkg };
}

/** A mutation stays frozen until a definitive response. A retry always uses the original body and key. */
export class ContentMutation {
  pending: { path: string; body: unknown; key: string } | null = null;
  busy = false;
  blocked = false;
  constructor(private readonly transport: Transport) {}
  async send(path?: string, body?: unknown) {
    if (this.busy || this.blocked) return null;
    if (!this.pending) {
      if (!path) return null;
      this.pending = { path, body: structuredClone(body), key: newRequestKey() };
    }
    const p = this.pending;
    const gen = currentGen();
    this.busy = true;
    try {
      const r = await this.transport.request('POST', p.path, { body: p.body, key: p.key });
      if (gen !== currentGen()) return null;
      if (r.kind === 'http') {
        if (r.body['error'] === 'request_key_reused' || r.body['error'] === 'request_in_unknown_state') this.blocked = true;
        else if (r.status < 500) this.pending = null;
      }
      return r;
    } finally { this.busy = false; }
  }
}
